import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import LogRocket from 'logrocket';

import { PageTitle, THEMES_DARK, useTheme } from '@utilities';

import { applyWorldPanelDocumentBranding, isWorldPanel } from '@helpers';

import Dashboard from './pages/Dashboard';
import Interceptors from './components/Interceptors';
import Login from './pages/Login';
import Plugins from './components/Plugins';
import Share from './pages/Share';

import '@utilities/_global.scss';

const App = () => {
  const theme = useTheme();
  const location = useLocation();

  const getPageTitle = (pathname, host = window.location.host) => {
    if (isWorldPanel(host)) return 'Worldpanel Insights';

    const isVerifiedVoices =
      host.includes('verifiedvoices') ||
      pathname.includes('/verifiedvoices') ||
      /\/dashboard\/(create-survey|survey\/\d+)/.test(pathname);

    return isVerifiedVoices ? 'Verified Voices' : 'Numerator Insights';
  };

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    document.addEventListener('click', handleLogRocketEvents);
    return () => document.removeEventListener('click', handleLogRocketEvents);
  }, []);

  useEffect(() => {
    if (theme === THEMES_DARK.value) {
      document.body.classList.add(THEMES_DARK.value);
    } else {
      document.body.classList.remove(THEMES_DARK.value);
    }
  }, [theme]);

  useEffect(() => {
    if (isWorldPanel()) applyWorldPanelDocumentBranding();
  }, []);

  const handleLogRocketEvents = ({ target }) => {
    const element = target?.closest('[data-log]');
    if (element) {
      const eventName = element.dataset.log;
      let eventProperties = element.dataset.logProperties;
      if (eventName && eventProperties) {
        try {
          eventProperties = JSON.parse(eventProperties);
          LogRocket.track(eventName, eventProperties);
        } catch (error) {
          console.error(error);
          LogRocket.track(eventName, { error: true });
        }
      } else if (eventName) {
        LogRocket.track(eventName);
      }
    }
  };

  return (
    <div id="app">
      <PageTitle title={pageTitle} />

      <Routes>
        <Route element={<Dashboard />} path="/dashboard/*" />
        <Route element={<Login />} path="/login" />
        <Route element={<Share />} path="/share/:token/:tab_id" />
        <Route element={<Share />} path="/share/:token/*" />
        <Route element={<Share />} path="/share/report/:token/:tab_id" />
        <Route element={<Share />} path="/share/report/:token/*" />
        <Route element={<Share type="narrative" />} path="/share/narrative/:token" />
        <Route element={<Share type="survey" />} path="/share/survey/:token/:survey_id/*" />
        <Route element={<Navigate replace to="/dashboard" />} path="/*" />
      </Routes>

      <Interceptors />
      <Plugins />
    </div>
  );
};

export default App;
